// The effect interpreter.
//
// Walks the Instruction list of a spell or ability and calls the primitives in
// ops.ts. It is deliberately total: an op it does not recognise is skipped and
// noted rather than thrown, so effect data compiled by a newer tool can be
// loaded by an older engine without taking the game down mid-turn.
//
// Selectors that require a choice were already resolved when the item was
// finalized on the chain (355.8) and arrive here as `chosen`. Everything else is
// evaluated now, against the board as it stands at resolution.

import { isSpellType, isUnitType } from './cards.ts';
import type {
  Ability,
  ActivationCost, Condition, DynamicAmount, Instruction, ObjectFilter, Selector,
} from './effects.ts';
import * as ops from './ops.ts';
import type { Rules } from './state.ts';
import {
  OPPONENT, abilitiesOf, activePassives, baseLocation, battlefieldLocation,
  bonusDamageFor,
  discardCandidates,
  boardObjects,
  canBeDealtDamage, canPay,
  cantBeChosenBy,
  cantBeMovedBy,
  isRestricted,
  countDynamic, effectiveMight, facedownAt, hasLethalDamage, keywordAmount,
  keywordsOf, matchesCardDef,
  matchesFilter,
  sacrificeCandidates,
  moveToZone, pay,
  testStaticCondition, totalCost, zoneList,
} from './state.ts';
import type {
  GameObject, GameState, Location, ObjectId, PendingChoice, PlayerId, SpendPurpose,
} from './types.ts';
import { DECLINE, DOMAINS, sameLocation } from './types.ts';
import { nextInt } from './rng.ts';

export interface EffectContext {
  state: GameState;
  rules: Rules;
  /** The card or permanent the ability belongs to (its source). */
  source: GameObject;
  controller: PlayerId;
  /** Targets chosen during finalization, in the order the selectors appear. */
  chosen: ObjectId[];
  /** Set by `deal` so a following "if this kills it" can read it (Condition killedIt). */
  lastDealKilled: boolean;
  /** Cards a preceding `reveal` exposed, for takeRevealed / recycleRevealed. */
  revealed: ObjectId[];
  /** Set when an optional step actually happened, for "If you do, ...". */
  didIt: boolean;
  /** 356.2.b. Whether the controller took this card's optional additional cost. */
  paidAdditionalCost: boolean;
  /** 705. How many units the last damage instruction finished off. */
  lastDealKills?: number;
  /** 203.1. What this ability's cost was paid with. */
  paidWith?: ObjectId[];
  /** 204.3. What a preceding "pay any amount" paid, for "that much" to read. */
  paidAmount?: number;
  /**
   * 152. The battlefield an earlier instruction chose.
   *
   * Kept apart from `chosen` because a place and an object are different
   * referents: "choose a battlefield ... you may choose a unit ... they play
   * that unit to *that battlefield*" picks two things, and the second pick
   * replaces the first in `chosen` while the place still has to be there.
   */
  chosenPlace?: Location;
}

/**
 * 355.3 / 424. Thrown to abandon a resolution that has reached a question.
 *
 * Not an error: the caller catches it, rolls the state back to before the
 * resolution began, and records the question. Throwing is what unwinds the
 * recursion in `runEffects` -- `if`, `repeat` and `forEach` all nest, and
 * there is no other way out of the middle of them without rewriting the
 * resolver as a state machine.
 */
export class NeedsChoice {
  // Written out rather than as a parameter property: node runs this file
  // directly, and strip-only TypeScript has no way to compile that shorthand.
  readonly choice: PendingChoice;

  constructor(choice: PendingChoice) {
    this.choice = choice;
  }
}

/**
 * Takes the next answer for a choice, asking for one if it is not already in.
 *
 * Answers accumulate on the state across replays, so the second time through
 * this returns immediately for every question already settled and only the
 * newest one suspends. A logged answer that is no longer on offer is ignored
 * rather than trusted -- the log is state like any other and `applyAction`
 * must not be able to smuggle an illegal pick past the resolver.
 */
function answer(
  ctx: EffectContext, options: number[], prompt: string,
  modal: { labels?: string[] } | null,
  /** 133.8. The words on offer, when the choice is naming rather than picking. */
  names?: string[],
  /**
   * 463. Who is being asked, when it is not the ability's controller: "the
   * defender must kill one of their units here" is the defender's decision.
   */
  who?: PlayerId,
  /**
   * 436.1.a. The words on the button that ends an optional choice, when there
   * is one. Its answer is `DECLINE`, which no object id can be.
   */
  decline?: string,
): number {
  const state = ctx.state;
  const logged = state.choiceLog[state.choiceCursor];
  if (logged !== undefined) {
    state.choiceCursor++;
    if (options.includes(logged)) return logged;
    if (decline !== undefined && logged === DECLINE) return DECLINE;
    // A stale answer falls back to the canonical pick rather than throwing:
    // the replay must reach the same number of questions as the first pass.
    return options[0]!;
  }
  throw new NeedsChoice({
    player: who ?? ctx.controller,
    prompt,
    source: ctx.source.id,
    // Modes are offered by number, and the numbers are indices into the card's
    // own list -- so what is offered is the indices that are still available,
    // not a count, or "choose two" would offer the first mode twice.
    ...(modal
      ? { modes: options, ...(modal.labels ? { modeLabels: modal.labels } : {}) }
      : names ? { names }
      : { cards: options }),
    ...(decline !== undefined ? { decline } : {}),
  });
}

/**
 * 704. Runs a permanent's replacement for a chosen effect, if it has one.
 *
 * Returns whether the original instruction should be skipped. Lives here
 * rather than in `ops` because a replacement runs instructions of its own, and
 * `ops` deliberately knows nothing about the resolver.
 *
 * The replacement's own effects act on the protected permanent, not on
 * whatever the original instruction had chosen -- "give **me** +3 [M]" is
 * about the card the passive is printed on.
 */
function replaceChosen(
  ctx: EffectContext, target: GameObject,
  of: 'stun' | 'mightPenalty' | 'returnToHand',
): boolean {
  for (const e of activePassives(ctx.state, ctx.rules, target)) {
    if (e.p !== 'replaceChosen' || !e.of.includes(of)) continue;
    runEffects({ ...ctx, source: target, chosen: [target.id] }, e.effects);
    return true;
  }
  return false;
}

/**
 * 424. Looks at the top card before a reveal, and offers to recycle it.
 *
 * The offer belongs to the player about to reveal, and it is a real question:
 * digging past a card is the whole point, and taking it always would make the
 * permanent a downside as often as an upside.
 */
function peekBeforeReveal(ctx: EffectContext, who: PlayerId): void {
  const { state, rules } = ctx;
  const watching = boardObjects(state).some((o) =>
    o.controller === who
    && activePassives(state, rules, o).some((e) => e.p === 'peekBeforeReveal'));
  if (!watching) return;

  const deck = zoneList(state, 'mainDeck', who);
  const top = deck[deck.length - 1];
  if (top === undefined) return;
  const card = state.objects[top];
  if (!card) return;

  const name = rules.index.get(card.card).name;
  const pick = answer(
    ctx, [0, 1], `Recycle ${name} from the top of your deck?`,
    { labels: ['Recycle it', 'Leave it'] }, undefined, who,
  );
  if (pick === 0) ops.recycle(state, rules, [top]);
}

/** Evaluates one condition against a context, for callers outside this file. */
export function conditionHolds(ctx: EffectContext, cond: Condition): boolean {
  return testCondition(ctx, cond);
}


/**
 * Whose prevention would apply to damage from this effect.
 *
 * A prevention protects its own controller's board, so the question is who
 * owns what is being hit -- not who is dealing it.
 */
function target0Controller(state: GameState, ctx: EffectContext): PlayerId {
  const first = ctx.chosen[0] !== undefined ? state.objects[ctx.chosen[0]] : undefined;
  return first ? first.controller : OPPONENT(ctx.controller);
}


/** Every `choose` selector an instruction list will ask for, in order. */
function choiceSelectorsOf(
  effects: readonly Instruction[],
): Extract<Selector, { s: 'choose' }>[] {
  const out: Extract<Selector, { s: 'choose' }>[] = [];
  const visit = (node: unknown): void => {
    if (Array.isArray(node)) {
      for (const v of node) visit(v);
      return;
    }
    if (!node || typeof node !== 'object') return;
    const rec = node as Record<string, unknown>;
    const target = rec.target as Selector | undefined;
    if (target && target.s === 'choose') out.push(target);
    for (const v of Object.values(rec)) visit(v);
  };
  visit(effects);
  return out;
}

/**
 * 355.3. A mode's own targets, asked for once the mode is known.
 *
 * Every other target in the game is settled before the card reaches the chain
 * -- `targetVariants` enumerates them and picking the action is picking the
 * target (355, and 809 prices a play by what it aims at, so it has to be).
 * A modal card cannot work that way: which target is even legal depends on
 * which mode is taken, and the mode is not chosen until the card resolves.
 *
 * So a modal card is enumerated with no targets at all, and until now that is
 * exactly what its modes got. Mesmerize's "give an enemy unit -2 [M] this
 * turn" read an empty `chosen`, found nothing to give it to, and did nothing:
 * the player was never asked which enemy, and the mode silently had no effect.
 * The mode was chosen, the spell went to the trash, and the board was
 * unchanged -- which from the table is indistinguishable from a client that
 * dropped the click.
 *
 * Asking here is safe without a fresh gate: `chooseMode` is in `ASKING_OPS`,
 * so any item that can reach this line was snapshotted before it ran and a
 * `NeedsChoice` thrown from it has somewhere to roll back to.
 *
 * Only when nothing was chosen ahead of time. A card that targets outside its
 * modes as well would already have those picks in `chosen`, and appending to
 * them would shift what every `count` slices off the front. No printed card
 * does both today, and this leaves that case exactly as it was rather than
 * guessing at it.
 */
function modeContext(ctx: EffectContext, mode: readonly Instruction[]): EffectContext {
  if (ctx.chosen.length > 0) return ctx;
  const wanted = choiceSelectorsOf(mode);
  if (wanted.length === 0) return ctx;

  const picked: ObjectId[] = [];
  for (const sel of wanted) {
    for (let n = 0; n < (sel.count ?? 1); n++) {
      // Same ordering rule as `autoTargets` and `retarget`: a later selector
      // can refer back to an earlier pick ("an enemy unit at its location").
      const already = picked[0] !== undefined ? ctx.state.objects[picked[0]] : undefined;
      const options = candidates(ctx.state, ctx.rules, ctx.source, sel.of, already)
        .map((o) => o.id)
        .filter((id) => !picked.includes(id));
      if (options.length === 0) break;
      // One legal target is not a decision, and asking would put an answer in
      // the log for a question with a single button on it.
      picked.push(options.length === 1
        ? options[0]!
        : answer(ctx, options, 'Choose a target.', null));
    }
  }
  return picked.length > 0 ? { ...ctx, chosen: [...ctx.chosen, ...picked] } : ctx;
}

export function runEffects(ctx: EffectContext, effects: readonly Instruction[]): void {
  for (const instr of effects) runInstruction(ctx, instr);
}

function runInstruction(ctx: EffectContext, instr: Instruction): void {
  const { state, rules, source, controller } = ctx;

  switch (instr.op) {
    case 'draw': {
      const n = instr.countFrom
        ? dynamicAmount(ctx, instr.countFrom) * (instr.count || 1)
        : instr.count;
      ops.draw(state, rules, playerFor(ctx, instr.who), n);
      break;
    }

    case 'swapMight': {
      const [a, b] = select(ctx, instr.target);
      if (a && b) {
        // Both read before either changes, or the second swap would use the
        // value the first one just wrote.
        const mightA = effectiveMight(state, rules, a);
        const mightB = effectiveMight(state, rules, b);
        ops.addMightMod(state, a, mightB - mightA, instr.duration, source.id);
        ops.addMightMod(state, b, mightA - mightB, instr.duration, source.id);
        ctx.didIt = true;
      }
      state.version++;
      break;
    }

    case 'double':
      // 432.1.a. Doubling adds a modification equal to the current value, so
      // later changes do not re-double it.
      for (const target of select(ctx, instr.target)) {
        ops.addMightMod(
          state, target, effectiveMight(state, rules, target), instr.duration, source.id,
        );
      }
      break;

    case 'discard':
      // 203. "They discard that card": a discard the effect has already
      // settled on, rather than a free choice off the top of their hand.
      if (instr.target) {
        for (const card of select(ctx, instr.target)) {
          moveToZone(state, rules, card, { kind: 'trash', player: card.owner });
        }
        state.version++;
        break;
      } {
      const player = playerFor(ctx, instr.who);
      const hand = [...zoneList(state, 'hand', player)];
      // Discards from a private zone are not targeted (355.10.a); the engine
      // picks for the AI, and a UI can prompt before submitting the action.
      const picked: ObjectId[] = [];
      for (let i = 0; i < instr.count && hand.length > 0; i++) {
        picked.push(hand.splice(nextInt(state, hand.length), 1)[0]!);
      }
      ops.discard(state, rules, player, picked);
      // 203. The discarded cards become the referent: "do the following based
      // on the discarded card's type" has nothing else to read.
      if (picked.length > 0) ctx.chosen = picked;
      break;
    }

    case 'deal': {
      ctx.lastDealKilled = false;
      // 706. Counted before the damage lands, so "for each time this spell has
      // dealt damage this turn" reads the instances *before* this one -- which
      // is what "additional" means on the card that asks.
      const dealtBefore = source.damageInstancesThisTurn ?? 0;
      // 706. A standing "deal 1 Bonus Damage" adds to every Deal, so it is
      // applied here rather than at each of the many sites that build one.
      // 706. Standing bonus damage, plus a one-shot "the next spell you play
      // this turn deals N Bonus Damage" -- which only a spell may claim, and
      // which is spent the first time that spell deals.
      let bonus = bonusDamageFor(state, rules, controller);
      if (isSpellType(rules.index.get(source.card).type)) {
        const i = state.turnEffects.findIndex(
          (e) => e.k === 'nextSpellBonusDamage' && e.player === controller,
        );
        const pending = i >= 0 ? state.turnEffects[i] : undefined;
        if (pending && pending.k === 'nextSpellBonusDamage') {
          bonus += pending.amount;
          state.turnEffects.splice(i, 1);
        }
      }
      // 706. And extra damage this instruction carries in its own right:
      // "This deals 1 Bonus Damage for each card with this name in your trash."
      if (instr.bonusPer) {
        // The source's own instance count is read from before this deal was
        // counted, so "additional for each time" does not include this time.
        const per = instr.bonusPer.from;
        bonus += instr.bonusPer.amount
          * (typeof per === 'object' && 'selfDamageInstancesThisTurn' in per
            ? dealtBefore
            : dynamicAmount(ctx, per));
      }
      source.damageInstancesThisTurn = dealtBefore + 1;
      const amount = (instr.amountFrom
        ? dynamicAmount(ctx, instr.amountFrom)
        : instr.amount) + bonus;
      // 705. A standing prevention stops damage from spells and abilities for
      // the rest of the turn. Combat damage has its own path and is untouched.
      const prevented = state.turnEffects.some((e) =>
        e.k === 'preventEffectDamage' && e.player === target0Controller(state, ctx));

      for (const target of select(ctx, instr.target)) {
        // 705. Some units cannot be dealt damage at all.
        if (prevented) continue;
        if (!canBeDealtDamage(state, rules, target, controller)) continue;
        // 705/363.3. "The next time that unit would be dealt damage this turn,
        // prevent it" -- armed for one occurrence and spent by it, whether or
        // not the replacement does anything visible.
        const armed = state.turnEffects.findIndex(
          (e) => e.k === 'nextDamage' && e.object === target.id,
        );
        if (armed >= 0) {
          const effect = state.turnEffects[armed]!;
          state.turnEffects.splice(armed, 1);
          if (effect.k === 'nextDamage') {
            const from = state.objects[effect.source];
            if (from) {
              runEffects({
                ...ctx, source: from, controller: effect.player, chosen: [target.id],
              }, effect.effects);
            }
          }
          continue;
        }
        // 705. A multiplier scales the hit before anything absorbs it: "double
        // all damage that would be dealt to it" doubles what arrives, not what
        // is left after a shield.
        let landing = amount;
        for (const e of state.turnEffects) {
          if (e.k === 'damageMultiplier' && e.object === target.id) landing *= e.factor;
        }
        // 705. A shield absorbs what it can and keeps the rest for later, so
        // it is decremented rather than spent whole.
        for (const e of state.turnEffects) {
          if (e.k !== 'damageShield' || e.object !== target.id || landing <= 0) continue;
          const soaked = Math.min(e.amount, landing);
          e.amount -= soaked;
          landing -= soaked;
        }
        state.turnEffects = state.turnEffects.filter(
          (e) => e.k !== 'damageShield' || e.amount > 0,
        );
        if (landing <= 0) continue;
        ops.deal(state, target, landing, source.id);
        if (hasLethalDamage(state, rules, target)) ctx.lastDealKilled = true;
      }
      break;
    }

    case 'fight': {
      // 417.6.b.3. Each unit is the source of the damage it deals, so this is
      // two Deal actions rather than one from the spell that arranged them.
      const a = select(ctx, instr.a)[0];
      const b = select(ctx, instr.b)[0];
      if (a && b) {
        const mightA = effectiveMight(state, rules, a);
        const mightB = effectiveMight(state, rules, b);
        ops.deal(state, b, mightA, a.id);
        ops.deal(state, a, mightB, b.id);
      }
      break;
    }

    case 'hide': {
      // 811. Put a card from hand facedown at a battlefield the controller
      // holds that has none already.
      const hand = zoneList(state, 'hand', controller);
      const bf = state.battlefields.find((b) =>
        b.controller === controller && facedownAt(state, b.index).length === 0);
      const pick = hand.map((id) => state.objects[id]).find((o): o is GameObject => {
        if (!o) return false;
        return !instr.filter || matchesFilter(state, rules, o, instr.filter, source);
      });
      if (bf && pick) {
        moveToZone(state, rules, pick, { kind: 'facedown', battlefield: bf.index });
        pick.controller = controller;
        pick.statuses = ['facedown'];
        state.events.push({
          t: 'hide', player: controller, object: pick.id, battlefield: bf.index,
        });
      }
      break;
    }

    case 'perKill': {
      // 705. Once for each unit the damage before it killed. Read from the
      // context rather than from the board: by now they are gone.
      for (let i = 0; i < (ctx.lastDealKills ?? 0); i++) runEffects(ctx, instr.body);
      break;
    }

    case 'dealSplit': {
      // 355.14. Spread the damage one point at a time over the chosen units,
      // finishing one off before moving on, which is what a player would do.
      const targets = select(ctx, instr.target);
      let remaining = instr.amountFrom
        ? dynamicAmount(ctx, instr.amountFrom) : instr.amount;
      let kills = 0;
      for (const target of targets) {
        if (remaining <= 0) break;
        const needed = Math.max(1, ops.obj(state, target.id).damage >= 0
          ? Math.max(1, mightGap(ctx, target)) : 1);
        const amount = Math.min(remaining, needed);
        ops.deal(state, target, amount, source.id);
        remaining -= amount;
        if (hasLethalDamage(state, rules, target)) kills++;
      }
      ctx.lastDealKills = kills;
      ctx.lastDealKilled = kills > 0;
      break;
    }

    case 'heal':
      for (const target of select(ctx, instr.target)) ops.heal(state, target);
      break;

    case 'stun':
      for (const target of select(ctx, instr.target)) {
        if (replaceChosen(ctx, target, 'stun')) continue;
        ops.stun(state, target);
      }
      break;

    case 'kill':
      for (const target of select(ctx, instr.target)) ops.kill(state, rules, target, source.id);
      break;

    case 'banish':
      // 447. Banished *by this source*, which is what "banished with me" reads.
      for (const target of select(ctx, instr.target)) ops.banish(state, rules, target, source.id);
      break;

    case 'moveAllCards': {
      for (const o of Object.values(state.objects)) {
        if (!o || !matchesFilter(state, rules, o, instr.filter, source)) continue;
        moveToZone(state, rules, o, { kind: instr.to, player: o.owner });
      }
      break;
    }

    case 'mightMod': {
      // 143. A standing "your penalties bite harder" adds to every reduction
      // this player's effects hand out -- and to no increase, which is what
      // keeps it off the cards that give +[M].
      let harder = 0;
      for (const o of boardObjects(state)) {
        if (o.controller !== controller) continue;
        for (const e of activePassives(state, rules, o)) {
          if (e.p === 'bonusMightPenalty') harder += e.amount;
        }
      }
      for (const target of select(ctx, instr.target)) {
        // Only a reduction is caught: the card names "-[M]", and a buff aimed
        // at the same unit is not something it needs protecting from.
        if (instr.amount < 0 && replaceChosen(ctx, target, 'mightPenalty')) continue;
        // "+1 [M] for each of your Mighty units" scales with the board.
        let amount = instr.amountFrom
          ? instr.amount * dynamicAmount(ctx, instr.amountFrom)
          : instr.amount;
        if (amount < 0) amount -= harder;
        // "to a minimum of N Might": never reduce below the floor.
        if (instr.min !== undefined && amount < 0) {
          const current = Math.max(0, mightNow(ctx, target));
          amount = Math.max(amount, instr.min - current);
        }
        ops.addMightMod(state, target, amount, instr.duration, source.id);
      }
      break;
    }

    case 'grantKeyword': {
      // Read before the loop: "equal to its cost" is the cost of the card that
      // was chosen, and granting does not change it.
      const value = instr.valueFrom
        ? dynamicAmount(ctx, instr.valueFrom) : (instr.value ?? 1);
      for (const target of select(ctx, instr.target)) {
        ops.grantKeyword(state, target, instr.keyword, value, instr.duration, source.id);
      }
      break;
    }

    case 'grantMyKeywords': {
      // Read once, before anything is granted: granting can change what the
      // source has if it is also a legal target.
      const mine = [...keywordsOf(state, rules, source)];
      for (const target of select(ctx, instr.target)) {
        for (const kw of mine) {
          ops.grantKeyword(
            state, target, kw, keywordAmount(state, rules, source, kw),
            instr.duration, source.id,
          );
        }
      }
      state.version++;
      break;
    }

    case 'ready':
      for (const target of select(ctx, instr.target)) {
        // 414. "I can't be readied" refuses every source of readying. `ops.ready`
        // has no view of passives, so the test sits with the callers that do.
        if (activePassives(state, rules, target).some((e) => e.p === 'cantBeReadied')) {
          continue;
        }
        // 414. And a standing prohibition someone else is imposing: "spells and
        // abilities can't ready enemy units and gear". Only effects are caught
        // -- the normal readying step in the Beginning Phase is untouched.
        if (isRestricted(state, rules, 'ready', target.controller, { subject: target })) {
          continue;
        }
        ops.ready(state, target);
      }
      break;

    case 'exhaust':
      for (const target of select(ctx, instr.target)) ops.exhaust(state, target);
      break;

    case 'move': {
      // 144. "To a single location": the first unit's destination decides where
      // the whole group goes.
      let shared: Location | null = null;
      for (const target of select(ctx, instr.target)) {
        const to = instr.to === 'base' ? baseLocation(target.controller)
          : instr.to === 'here' ? source.location
            : instr.to === 'paidWithLocation'
              ? (select(ctx, { s: 'paidWith' })[0]?.location ?? null)
              : instr.to === 'chosenBattlefield'
              ? (select(ctx, { s: 'chosen' })
                .find((o) => rules.index.get(o.card).type === 'battlefield')?.location ?? null)
              : instr.to === 'chosenLocation' ? chosenLocation(ctx)
              : instr.to === 'oneBattlefield'
                ? (shared ??= pickBattlefield(ctx, target))
              : instr.to === 'occupiedBattlefield'
                ? pickBattlefield(ctx, target, false, true)
                : pickBattlefield(ctx, target, instr.to === 'friendlyBattlefield');
        // 144. A unit an effect may not move stays where it is.
        if (cantBeMovedBy(state, rules, target, controller)) continue;
        if (to && !sameLocation(to, target.location)) ops.move(state, target, to, controller);
      }
      break;
    }

    case 'recall':
      for (const target of select(ctx, instr.target)) {
        ops.recallToBase(state, target);
        if (instr.exhausted) ops.exhaust(state, target);
      }
      break;

    case 'addResource': {
      const domain = instr.domain === 'self'
        // 164.2.b.1 / 135.2.e.6: [C] is Power of the source's own domain.
        ? (rules.index.get(source.card).domains[0] ?? 'any')
        : (instr.domain ?? null);
      const energy = instr.energyFrom
        ? dynamicAmount(ctx, instr.energyFrom) : (instr.energy ?? 0);
      const power = instr.powerFrom
        ? dynamicAmount(ctx, instr.powerFrom) : (instr.power ?? 0);
      // 429. "Your Gold [Add] an additional [1]." An aura on someone else's
      // board object that tops up what this ability produces, so it is read
      // here rather than baked into the ability it modifies.
      let bonusEnergy = 0;
      let bonusPower = 0;
      for (const watcher of boardObjects(state)) {
        if (watcher.controller !== controller) continue;
        for (const e of activePassives(state, rules, watcher)) {
          if (e.p !== 'extraResource') continue;
          if (!matchesFilter(state, rules, source, e.filter, watcher)) continue;
          bonusEnergy += e.energy ?? 0;
          bonusPower += e.power ?? 0;
        }
      }
      ops.addResources(
        state, controller, energy + bonusEnergy, power + bonusPower,
        domain, instr.only ?? null,
      );
      break;
    }

    case 'asPlayer':
      runEffects({ ...ctx, controller: playerFor(ctx, instr.who) }, instr.then);
      break;

    case 'eachPlayer': {
      // Turn order starting with the controller, which is the order the rules
      // use for any simultaneous choice (322.4).
      for (const p of [controller, OPPONENT(controller)]) {
        runEffects({ ...ctx, controller: p }, instr.then);
      }
      break;
    }

    case 'banishZones': {
      // 447. Read into an array first: banishing moves cards out of the very
      // list being walked.
      const whose = playerFor(ctx, instr.who);
      for (const zone of instr.zones) {
        for (const id of [...zoneList(state, zone === 'hand' ? 'hand' : 'trash', whose)]) {
          const card = state.objects[id];
          if (card) moveToZone(state, rules, card, { kind: 'banishment', player: card.owner });
        }
      }
      state.version++;
      break;
    }

    case 'discardHand': {
      const who = playerFor(ctx, instr.who);
      for (const id of [...zoneList(state, 'hand', who)]) {
        const card = state.objects[id];
        if (card) moveToZone(state, rules, card, { kind: 'trash', player: who });
      }
      state.version++;
      break;
    }

    case 'mill': {
      const who = playerFor(ctx, instr.who);
      for (let i = 0; i < instr.count; i++) {
        const deck = zoneList(state, 'mainDeck', who);
        const id = deck[deck.length - 1];
        if (id === undefined) break;
        const card = state.objects[id];
        if (!card) break;
        moveToZone(state, rules, card, { kind: 'trash', player: who });
      }
      state.version++;
      break;
    }

    case 'channel':
      ops.channel(
        state, rules, playerFor(ctx, instr.who), instr.count, instr.exhausted ?? false,
      );
      break;

    case 'recycle': {
      // 416. "They recycle that card": one already settled on, wherever it is.
      if (instr.target) {
        ops.recycle(state, rules, select(ctx, instr.target).map((o) => o.id));
        break;
      }
      const zone = instr.from === 'trash' ? 'trash' : 'hand';
      const list = [...zoneList(state, zone, controller)];
      // 416.4: recycle as many as possible.
      ops.recycle(state, rules, list.slice(0, instr.count));
      break;
    }

    case 'gainPoints':
      ops.gainPoints(
        state, rules, playerFor(ctx, instr.who),
        instr.amountFrom ? instr.amount * dynamicAmount(ctx, instr.amountFrom) : instr.amount,
      );
      break;

    case 'gainXp': {
      const gained = instr.amountFrom
        ? instr.amount * dynamicAmount(ctx, instr.amountFrom)
        : instr.amount;
      state.players[controller].xp += gained;
      state.players[controller].xpGainedThisTurn += gained;
      state.version++;
      break;
    }

    case 'buff':
      for (const target of select(ctx, instr.target)) {
        // 137.2. The cap belongs to the unit being buffed, not to the buffer.
        let limit = 1;
        for (const e of activePassives(state, rules, target)) {
          if (e.p === 'multiBuff') limit = Number.MAX_SAFE_INTEGER;
        }
        ops.buff(state, target, limit);
      }
      break;

    case 'spendBuff':
      state.pendingTriggers.push({ on: 'youSpendBuff', player: controller });
      for (const target of select(ctx, instr.target)) ops.spendBuff(target);
      break;

    case 'createToken': {
      // 144. "There" is a place already named: one an earlier instruction
      // chose, or -- for a trigger that fired *on* a move -- the location the
      // source moved away from. `movedFrom` is what carries the far end of a
      // move, because a location can only say where something is now.
      const there = ctx.chosenPlace ?? source.movedFrom ?? source.location;
      const at = instr.where === 'there' ? there
        : instr.where === 'here' ? source.location
          : undefined;
      const where = at ?? baseLocation(controller);
      const many = instr.countFrom
        ? dynamicAmount(ctx, instr.countFrom) : (instr.count ?? 1);
      // The tokens become the referent, so "then ready two of them" can name
      // them -- nothing else in the sentence knows which units are new.
      // 704. "You may play that token and an additional copy of it instead."
      // Found before any token is made, so both copies arrive together and
      // land in `chosen` as one group.
      const doubler = boardObjects(state).find((o) => {
        if (o.controller !== controller) return false;
        return activePassives(state, rules, o).some(
          (e) => e.p === 'duplicateTokens'
            && (!e.oncePerTurn || !o.usedThisTurn.includes('duplicateTokens')),
        );
      });
      if (doubler) doubler.usedThisTurn.push('duplicateTokens');
      const each = doubler ? 2 : 1;

      const made: ObjectId[] = [];
      for (let i = 0; i < many * each; i++) {
        made.push(
          ops.createToken(state, rules, instr.token, controller, where, instr.ready ?? false).id,
        );
      }
      if (made.length > 0) ctx.chosen = made;
      break;
    }

    case 'counter': {
      // 425. Remove the targeted chain item; the card goes to its owner's trash.
      for (const target of select(ctx, instr.target)) {
        const i = state.chain.findIndex((item) => item.source === target.id);
        if (i < 0) continue;
        // 425. Some spells simply cannot be countered.
        // 425. The protection can be the target's own, or an aura someone else
        // is holding over it.
        const shielded = activePassives(state, rules, target)
          .some((e) => e.p === 'cantBeCountered' && !e.filter)
          || boardObjects(state).some((o) => activePassives(state, rules, o)
            .some((e) => e.p === 'cantBeCountered' && e.filter
              && matchesFilter(state, rules, target, e.filter, o)));
        if (shielded) continue;
        // 355.12. "Unless its controller pays [2]": nothing here can prompt,
        // so they pay whenever they can -- which is what a player does when
        // the alternative is losing the spell.
        if (instr.unlessPays) {
          const theirs = state.players[target.controller].pool;
          const price = {
            energy: instr.unlessPays.energy,
            power: instr.unlessPays.power,
            powerDomains: [],
          };
          if (pay(theirs, price)) continue;
        }
        state.events.push({ t: 'counter', item: state.chain[i]!.id });
        state.chain.splice(i, 1);
        ops.obj(state, target.id);
        const card = state.objects[target.id];
        if (card) moveToZone(state, rules, card, { kind: 'trash', player: card.owner });
      }
      break;
    }

    case 'predict': {
      // 436. Look at the top card; you may recycle it. The engine recycles when
      // the card cannot be paid for this turn, which is the obvious heuristic.
      const deck = zoneList(state, 'mainDeck', controller);
      const top = deck[deck.length - 1];
      if (top === undefined) break;
      const def = rules.index.get(ops.obj(state, top).card);
      if (def.cost.energy > state.players[controller].pool.energy + 2) {
        ops.recycle(state, rules, [top]);
      }
      break;
    }

    case 'empower':
      for (const target of select(ctx, instr.target)) {
        // 441. The cap is the target's own, not the empowering card's.
        let limit = 1;
        for (const e of activePassives(state, rules, target)) {
          if (e.p === 'empowerLimit') limit = Math.max(limit, e.times);
        }
        if (ops.empower(state, target, limit)) ctx.didIt = true;
      }
      break;

    case 'disempower':
      for (const target of select(ctx, instr.target)) ops.disempower(state, target);
      break;

    case 'reveal': {
      // 424. Revealed cards stay in the deck until something moves them, so
      // this only records what was seen for a following takeRevealed.
      const who = playerFor(ctx, instr.who);
      // 424. "If you would reveal cards from a deck, look at the top card
      // first. You may recycle it." A replacement, so it runs before the
      // reveal and can change what the reveal finds.
      if (instr.from !== 'hand') peekBeforeReveal(ctx, who);
      ctx.revealed = instr.from === 'hand'
        ? [...zoneList(state, 'hand', who)]
        : ops.revealTop(state, who, instr.count);
      // 424. A card can care about being turned face up itself, which is not
      // the same event as its controller revealing something.
      for (const id of ctx.revealed) {
        state.pendingTriggers.push({ on: 'revealed', player: who, subject: id });
      }
      break;
    }

    case 'mayPlaySelfFromTrash': {
      // 356.4. The card has not reached the trash yet -- it is still the thing
      // resolving -- so the offer names it and waits for it to land there.
      // "Its controller may play this spell again" -- the offer belongs to
      // whoever controls what this spell chose, not to the caster.
      const offerTo = instr.who === 'chosenController'
        ? (ctx.chosen[0] !== undefined
          ? state.objects[ctx.chosen[0]]?.controller ?? controller
          : controller)
        : controller;
      state.turnEffects.push({
        k: 'playFromTrash', player: offerTo, object: source.id,
        price: instr.price,
      });
      break;
    }

    case 'nameIt': {
      // 133.8. Every tag, or every spell name, that the game prints: the card
      // says "name a tag", not "name one you can see".
      const words = new Set<string>();
      for (const def of rules.index.all()) {
        if (instr.what === 'tag') for (const t of def.tags) words.add(t);
        else if (isSpellType(def.type)) words.add(def.name);
      }
      const names = [...words].sort();
      if (names.length === 0) break;
      const pick = names.length === 1
        ? 0
        : answer(ctx, names.map((_, i) => i), `Name a ${instr.what}.`, null, names);
      const chosenName = names[pick];
      if (chosenName === undefined) break;
      source.named = chosenName;
      state.version++;
      break;
    }

    case 'revealTopRune': {
      // 424. The rune is named for what follows -- "based on its domain" --
      // and then goes to the bottom of the Rune Deck if the card says so.
      const deck = zoneList(state, 'runeDeck', controller);
      const top = deck[deck.length - 1];
      if (top === undefined) break;
      ctx.chosen = [top];
      state.pendingTriggers.push({ on: 'revealed', player: controller, subject: top });
      if (instr.recycle) {
        const card = state.objects[top];
        if (card) {
          moveToZone(state, rules, card, { kind: 'runeDeck', player: card.owner }, { toBottom: true });
        }
      }
      state.version++;
      break;
    }

    case 'revealUntil': {
      // 424. Down the deck a card at a time, stopping on the first match. The
      // whole run is left in `revealed`, so the instructions that follow can
      // take the match and recycle the rest exactly as after a plain reveal.
      const deck = zoneList(state, 'mainDeck', controller);
      const seen: ObjectId[] = [];
      for (let i = deck.length - 1; i >= 0; i--) {
        const id = deck[i]!;
        seen.push(id);
        const o = state.objects[id];
        if (o && matchesCardDef(rules.index.get(o.card), instr.filter)) break;
      }
      ctx.revealed = seen;
      for (const id of seen) {
        state.pendingTriggers.push({ on: 'revealed', player: controller, subject: id });
      }
      break;
    }

    case 'takeRevealed': {
      const wanted = ctx.revealed.filter((id) => {
        const o = state.objects[id];
        if (!o) return false;
        return !instr.filter || matchesCardFilter(ctx, o, instr.filter);
      });
      const takeCount = instr.count ?? 1;
      // Only a question when there is something to decide: a look that hands
      // over everything it found has nothing to ask about.
      const picks: ObjectId[] = [];
      const left = [...wanted];
      for (let n = 0; n < takeCount && left.length > 0; n++) {
        const id = left.length === 1 || left.length <= takeCount - n
          ? left[0]!
          : answer(ctx, left, 'Choose a card.', null);
        const at = left.indexOf(id);
        picks.push(...left.splice(at < 0 ? 0 : at, 1));
      }
      for (const pick of picks) {
        const o = state.objects[pick];
        if (o) {
          // 356. Taking a revealed card to the board is playing it, and a play
          // is paid for unless the card says "ignoring its cost". This used to
          // put it down free whatever the card said.
          if (instr.to === 'board' && !instr.free) {
            const price = totalCost(state, rules, rules.index.get(o.card), controller);
            if (instr.discount) {
              price.energy = Math.max(0, price.energy - instr.discount.energy);
              price.power = Math.max(0, price.power - instr.discount.power);
            }
            if (!pay(state.players[controller].pool, price)) continue;
          }
          ops.obj(state, pick);
          moveRevealed(ctx, o, instr.to ?? 'hand', instr.where === 'here');
          ctx.revealed = ctx.revealed.filter((id) => id !== pick);
          ctx.didIt = true;
        }
      }
      break;
    }

    case 'trashRevealed': {
      for (const id of ctx.revealed) {
        const o = state.objects[id];
        if (o) moveToZone(state, rules, o, { kind: 'trash', player: o.owner });
      }
      ctx.revealed = [];
      break;
    }

    case 'pickRevealed': {
      // Biggest first, which is the order to offer them in and the answer to
      // fall back on when this resolution is not one that can ask.
      const eligible = ctx.revealed
        .map((id) => state.objects[id])
        .filter((o): o is GameObject =>
          o !== undefined
          && (!instr.filter || matchesCardDef(rules.index.get(o.card), instr.filter)))
        .sort((a, b) =>
          rules.index.get(b.card).cost.energy - rules.index.get(a.card).cost.energy);

      // 424.2. "Look at the top 3 cards, choose one" is the choice the whole
      // instruction exists for, so it is asked rather than assumed -- one
      // question per card the effect takes, since taking two is two decisions.
      const want = instr.count ?? 1;
      const taken: GameObject[] = [];
      const pool = [...eligible];
      for (let n = 0; n < want && pool.length > 0; n++) {
        const pick = pool.length <= want - n
          ? pool[0]!.id
          : answer(ctx, pool.map((o) => o.id), 'Choose a card.', null);
        const at = pool.findIndex((o) => o.id === pick);
        taken.push(...pool.splice(at < 0 ? 0 : at, 1));
      }
      if (instr.to === 'chosen') {
        // Names them for what follows; the cards stay where they are.
        ctx.chosen = taken.map((o) => o.id);
        // 424. "Choose one, then recycle the rest": the pick has to leave the
        // revealed set, or "the rest" sweeps it up along with the others.
        if (instr.setAside) {
          ctx.revealed = ctx.revealed.filter((id) => !taken.some((o) => o.id === id));
        }
        break;
      }
      if (instr.to === 'recycle') {
        ops.recycle(state, rules, taken.map((o) => o.id));
      } else if (instr.to === 'banishment') {
        // 447. Through `ops.banish` rather than a bare zone move, so the
        // banish is announced and the card remembers what banished it -- which
        // is how "then play it" finds it again.
        for (const o of taken) ops.banish(state, rules, o, source.id);
      } else {
        for (const o of taken) moveToZone(state, rules, o, { kind: 'trash', player: o.owner });
      }
      ctx.revealed = ctx.revealed.filter((id) => !taken.some((o) => o.id === id));
      state.version++;
      break;
    }

    case 'keepRevealed':
      // 424.2. They never left the deck; this only ends the look.
      ctx.revealed = [];
      break;

    case 'unchosen': {
      // 416. Everything the filters name that the effect did not choose. The
      // hand counts, so the pool is every object rather than the board alone.
      const keep = new Set(ctx.chosen);
      const doomed: ObjectId[] = [];
      for (const o of Object.values(state.objects)) {
        if (!o || keep.has(o.id) || o.controller !== controller) continue;
        if (o.zone.kind !== 'board' && o.zone.kind !== 'hand') continue;
        if (instr.filters.some((f) => matchesFilter(state, rules, o, f, source))) {
          doomed.push(o.id);
        }
      }
      if (instr.action === 'kill') {
        for (const id of doomed) {
          const o = state.objects[id];
          if (o) ops.kill(state, rules, o, source.id);
        }
      } else {
        ops.recycle(state, rules, doomed);
      }
      break;
    }

    case 'recycleRevealed': {
      const seen = ctx.revealed.filter((id) => !!state.objects[id]);
      // 424. "You may recycle one or both of them." Each card is its own
      // question, and the ones declined stay where they are for the following
      // `keepRevealed` to put back. The old reading recycled everything the
      // look turned up without asking, which made the card look inert: two
      // cards went quietly to the bottom of the deck and nothing was offered.
      if (instr.may) {
        // 436.1.a. One decision taken with everything in view: the player
        // looks at all the cards, then recycles any number of them. Asking
        // card by card would be a run of blind questions -- deciding about the
        // first without being shown the second is a worse position than the
        // rules put the player in, and the wording ("one or both", "put those
        // you don't back in any order") only makes sense seeing all of them.
        const doomed: ObjectId[] = [];
        const left = seen.filter((id) => !!state.objects[id]);
        while (left.length > 0) {
          const pick = answer(
            ctx, left, 'Recycle any of these, or put them back.', null,
            undefined, undefined, 'Put them back',
          );
          if (pick === DECLINE) break;
          const at = left.indexOf(pick);
          if (at < 0) break;
          doomed.push(...left.splice(at, 1));
        }
        if (doomed.length > 0) ops.recycle(state, rules, doomed);
        const gone = new Set(doomed);
        ctx.revealed = ctx.revealed.filter((id) => !gone.has(id));
        break;
      }
      ops.recycle(state, rules, seen);
      ctx.revealed = [];
      break;
    }

    case 'returnToHand':
      for (const target of select(ctx, instr.target)) {
        if (replaceChosen(ctx, target, 'returnToHand')) continue;
        ops.returnToHand(state, rules, target);
      }
      break;

    case 'ownerPlays': {
      // 349. Straight onto the board rather than through the chain: the choice
      // has already been made and nothing here is interactive.
      for (const target of select(ctx, instr.target ?? { s: 'chosen' })) {
        const was = target.location;
        const owner = target.owner;
        // 356. "Ignoring Energy costs. (They must still pay Power costs.)"
        // A card whose Power cannot be found is simply not played.
        if (instr.payPower) {
          const price = rules.index.get(target.card).cost;
          const paid = pay(state.players[owner].pool, {
            energy: 0, power: price.power, powerDomains: price.powerDomains,
          });
          if (!paid) continue;
        }
        moveToZone(state, rules, target, { kind: 'board' });
        target.controller = owner;
        // The battlefield an earlier instruction chose, when the card names
        // one; otherwise where it was, any battlefield, or the owner's base.
        const picked = instr.where === 'chosenBattlefield'
          ? ctx.chosenPlace
            ?? select(ctx, { s: 'chosen' })
              .find((o) => rules.index.get(o.card).type === 'battlefield')?.location
            ?? null
          : null;
        target.location = picked
          ?? (instr.where === 'same' && was
            ? was
            : instr.where === 'battlefield' || instr.where === 'chosenBattlefield'
              ? (state.battlefields.length > 0 ? battlefieldLocation(0) : baseLocation(owner))
              : baseLocation(owner));
        // 143.4. It arrives as any unit does, so anything that changes that
        // ("I enter ready") still applies.
        target.exhausted = isUnitType(rules.index.get(target.card).type)
          && !activePassives(state, rules, target).some((e) => e.p === 'entersReady');
        state.events.push({
          t: 'play', player: owner, object: target.id, card: target.card,
          to: target.location,
        });
        state.pendingTriggers.push({ on: 'playerPlaysCard', player: owner, subject: target.id });
        ctx.didIt = true;
      }
      state.version++;
      break;
    }

    case 'moveCard': {
      // 416. The deck is ordered, so "on the top or bottom" is two different
      // destinations rather than one zone -- `moveToZone` appends, which is
      // the top, so the bottom has to be spliced in by hand.
      if (instr.to === 'deckTop' || instr.to === 'deckBottom') {
        for (const target of select(ctx, instr.target)) {
          moveToZone(state, rules, target, { kind: 'mainDeck', player: target.owner });
          if (instr.to === 'deckBottom') {
            const deck = zoneList(state, 'mainDeck', target.owner);
            const at = deck.indexOf(target.id);
            if (at >= 0) { deck.splice(at, 1); deck.unshift(target.id); }
          }
        }
        state.version++;
        break;
      }
      for (const target of select(ctx, instr.target)) {
        moveToZone(state, rules, target, { kind: instr.to, player: target.owner });
        ctx.didIt = true;
      }
      state.version++;
      break;
    }

    case 'ownerPlaces': {
      for (const target of select(ctx, instr.target ?? { s: 'chosen' })) {
        moveToZone(state, rules, target, { kind: 'mainDeck', player: target.owner },
          { toBottom: instr.to === 'bottom' });
        ctx.didIt = true;
      }
      state.version++;
      break;
    }

    case 'playFrom': {
      // Plays a matching card straight onto the board, skipping the chain.
      // Rules-correct play would use the chain (349), but nothing that reaches
      // here is interactive: the choice has already been made.
      const list = instr.zone === 'trash'
        ? [...zoneList(state, 'trash', controller)]
        : instr.zone === 'hand'
          ? [...zoneList(state, 'hand', controller)]
          : instr.zone === 'banishment'
            // 447. Banishment holds both players' cards, so the filter is the
            // only thing narrowing it -- "banished with me" is what does here.
            ? [...zoneList(state, 'banishment', controller),
              ...zoneList(state, 'banishment', (1 - controller) as PlayerId)]
            : [];
      const matching = list.filter((id) => {
        const o = state.objects[id];
        return o && (!instr.filter || matchesCardFilter(ctx, o, instr.filter));
      });
      // "Play all units banished with this" plays every match; everything else
      // plays the first one it finds.
      for (const pick of instr.all ? matching : matching.slice(0, 1)) {
        // 349. "You must pay its costs." Nothing here can prompt, so the card
        // is played whenever the pool covers it and skipped when it does not --
        // playing it for free would be strictly more generous than it prints.
        if (!instr.free) {
          const card = state.objects[pick];
          if (!card) continue;
          // 356.4. The card can name a price of its own, which replaces the
          // printed cost rather than adding to it.
          const price = instr.forCost
            ? {
              energy: instr.forCost.energy,
              power: instr.forCost.power,
              powerDomains: instr.forCost.domain && instr.forCost.domain !== 'any'
                ? [instr.forCost.domain]
                : rules.index.get(card.card).domains,
            }
            : totalCost(state, rules, rules.index.get(card.card), controller);
          if (instr.discount) {
            price.energy = Math.max(0, price.energy - instr.discount.energy);
            price.power = Math.max(0, price.power - instr.discount.power);
          }
          // 356. A discount measured from a referent -- "by the Might of the
          // unit you recycled" -- which only the running effect knows.
          if (instr.discountFrom) {
            price.energy = Math.max(0, price.energy - dynamicAmount(ctx, instr.discountFrom));
          }
          if (!pay(state.players[controller].pool, price)) continue;
        }
        const o = state.objects[pick];
        const def = o ? rules.index.get(o.card) : null;
        if (o && def && (isUnitType(def.type) || def.type === 'gear')) {
          moveRevealed(ctx, o, 'board');
          // 349.4. "To a battlefield you control" -- the first one held, or the
          // base when none is, which is where a play with nowhere to go lands.
          if (instr.to === 'friendlyBattlefield') {
            const held = state.battlefields.find((b) => b.controller === controller);
            if (held) o.location = battlefieldLocation(held.index);
          } else if (instr.to === 'here' && source.location?.kind === 'battlefield') {
            // 349.4. "Play a card ... here" -- the source's own battlefield.
            o.location = source.location;
          }
          ctx.didIt = true;
        } else if (o && def && isSpellType(def.type)) {
          // A spell played this way resolves straight away -- nothing that
          // reaches here is interactive -- rather than going on the chain.
          for (const ability of abilitiesOf(rules, o.card)) {
            if (ability.kind === 'spell') runEffects({ ...ctx, source: o }, ability.effects);
          }
          ctx.didIt = true;
        }
        // 353. "Recycle that spell after you play it." Where the card ends up
        // once it has been played, which only this instruction knows.
        if (o && instr.after) {
          if (instr.after === 'recycle') ops.recycle(state, rules, [o.id]);
          else moveToZone(state, rules, o, { kind: 'banishment', player: o.owner });
        }
      }
      break;
    }

    case 'swapLocations': {
      const first = select(ctx, instr.a)[0];
      const second = select(ctx, instr.b)[0];
      if (first && second) {
        // Both locations are read before either moves, or the second would be
        // sent to where the first had already gone.
        const wasFirst = first.location;
        const wasSecond = second.location;
        first.location = wasSecond;
        second.location = wasFirst;
        state.version++;
        ctx.didIt = true;
      }
      break;
    }

    case 'retarget': {
      for (const target of select(ctx, instr.target)) {
        const item = state.chain.find((c) => c.source === target.id);
        if (!item) continue;
        // The new controller picks afresh, from what is legal to them now.
        const owner = state.objects[item.source];
        if (!owner) continue;
        const picked: ObjectId[] = [];
        for (const sel of choiceSelectorsOf(item.effects ?? [])) {
          // Same ordering rule as `autoTargets`: a later selector can refer
          // back to an earlier pick.
          const already = picked[0] !== undefined ? state.objects[picked[0]] : undefined;
          const options = candidates(state, rules, owner, sel.of, already);
          const take = options.slice(0, sel.count ?? 1).map((o) => o.id);
          picked.push(...take);
        }
        item.targets = picked;
        state.version++;
        ctx.didIt = true;
      }
      break;
    }

    case 'replaceBattlefield': {
      const at = source.location?.kind === 'battlefield' ? source.location.index : null;
      const bf = at !== null ? state.battlefields[at] : undefined;
      if (bf) {
        const old = state.objects[bf.object];
        const replacement = ops.createToken(
          state, rules, instr.card, old?.controller ?? controller,
          { kind: 'battlefield', index: bf.index }, true,
        );
        bf.object = replacement.id;
        // 185. The token remembers what it stood in for, so a card that offers
        // to put the original back has something to name.
        if (old) replacement.replaced = old.id;
        // The old battlefield card leaves; the units standing there do not.
        if (old) moveToZone(state, rules, old, { kind: 'banishment', player: old.owner });
        state.version++;
        ctx.didIt = true;
      }
      break;
    }

    case 'defenderKills': {
      // 463. Only during a combat, and only from the units standing at it.
      const fight = state.combat;
      if (!fight) break;
      const at = battlefieldLocation(fight.battlefield);
      const theirs = boardObjects(state).filter((o) =>
        o.controller === fight.defender
        && isUnitType(rules.index.get(o.card).type)
        && sameLocation(o.location, at));
      let left = [...theirs];
      for (let n = 0; n < (instr.count ?? 1) && left.length > 0; n++) {
        const pick = left.length === 1
          ? left[0]!.id
          : answer(ctx, left.map((o) => o.id), 'Choose a unit to kill.', null,
            undefined, fight.defender);
        const victim = state.objects[pick];
        left = left.filter((o) => o.id !== pick);
        if (victim) ops.kill(state, rules, victim, source.id);
      }
      break;
    }

    case 'restoreBattlefield': {
      // 185. The source is the token standing in for a battlefield; putting
      // the original back is the same swap in the other direction.
      const back = source.replaced !== undefined ? state.objects[source.replaced] : undefined;
      const slot = source.location?.kind === 'battlefield'
        ? state.battlefields[source.location.index] : undefined;
      if (back && slot && slot.object === source.id) {
        moveToZone(state, rules, back, { kind: 'board' });
        back.location = { kind: 'battlefield', index: slot.index };
        slot.object = back.id;
        moveToZone(state, rules, source, { kind: 'banishment', player: source.owner });
        state.version++;
        ctx.didIt = true;
      }
      break;
    }

    case 'addBattlefield': {
      // 185. Only one copy of a given battlefield token is ever on the board.
      const already = state.battlefields
        .find((b) => state.objects[b.object]?.card === instr.card);
      const index = already ? already.index : state.battlefields.length;

      if (!already) {
        const spawned = ops.createToken(
          state, rules, instr.card, controller,
          { kind: 'battlefield', index }, true,
        );
        state.battlefields.push({
          index, object: spawned.id, controller: null, contestedBy: null,
          scoredThisTurn: [], conqueredThisTurn: [], showdownStaged: false, combatStaged: false,
        });
        ctx.didIt = true;
      }

      if (instr.enterThere) {
        source.location = { kind: 'battlefield', index };
      }
      state.version++;
      break;
    }

    case 'becomeCopy': {
      const model = select(ctx, instr.of)[0];
      if (model) {
        for (const target of select(ctx, instr.target)) {
          if (target.id === model.id) continue;
          // 818. Remember what it was, so the copy can be undone when the
          // Equipment holding it in place comes off. Only the first copy is
          // recorded: a second one is still standing on the original card.
          if (instr.whileAttached && !target.copiedWhileAttached) {
            target.copiedWhileAttached = { was: target.card, by: source.id };
          }
          target.card = model.card;
          state.version++;
          ctx.didIt = true;
        }
      }
      break;
    }

    case 'gainControl': {
      const to = instr.who ? playerFor(ctx, instr.who) : controller;
      for (const target of select(ctx, instr.target)) {
        if (target.controller === to) continue;
        // 187.4. Borrowed control remembers who to give it back to, and what
        // ends the loan. A second loan does not overwrite the first owner:
        // the card goes back to whoever had it before anyone borrowed it.
        if (instr.until && !target.borrowedControl) {
          target.borrowedControl = {
            from: target.controller, until: instr.until, by: source.id,
          };
        }
        target.controller = to;
        state.version++;
        ctx.didIt = true;
      }
      break;
    }

    case 'detach': {
      // 818. Named from the attachment's side: each one comes off its own host.
      if (instr.of) {
        for (const card of select(ctx, instr.of)) {
          if (card.attachedTo === null) continue;
          const host = state.objects[card.attachedTo];
          if (host) host.attachments = host.attachments.filter((a) => a !== card.id);
          card.attachedTo = null;
          ctx.didIt = true;
          state.version++;
        }
        break;
      }
      for (const host of select(ctx, instr.from)) {
        // The first attachment that matches: "an Equipment" is one of them,
        // not all of them.
        const id = host.attachments.find((a) => {
          const card = state.objects[a];
          return card !== undefined
            && (!instr.what || matchesFilter(state, rules, card, instr.what, source));
        });
        const card = id !== undefined ? state.objects[id] : undefined;
        if (!card) continue;
        host.attachments = host.attachments.filter((a) => a !== id);
        card.attachedTo = null;
        ctx.didIt = true;
      }
      state.version++;
      break;
    }

    case 'attachFrom': {
      const donor = select(ctx, instr.from)[0];
      const host = select(ctx, instr.to)[0];
      const moving = donor?.attachments
        .map((id) => state.objects[id])
        .find((o): o is GameObject => Boolean(o));
      if (donor && host && moving && host.id !== donor.id) {
        donor.attachments = donor.attachments.filter((x) => x !== moving.id);
        moving.attachedTo = host.id;
        moving.attachedTurn = state.turn;
        if (!host.attachments.includes(moving.id)) host.attachments.push(moving.id);
        moving.location = host.location;
        state.version++;
        ctx.didIt = true;
      }
      break;
    }

    case 'equipDiscounted': {
      // 821.1.c. The cheapest Equipment this player controls that they can
      // still afford after the discount. Nothing here can prompt, so it takes
      // the one that leaves the most resources behind.
      const pool = state.players[controller].pool;
      const options = boardObjects(state)
        .filter((o) => o.controller === controller && o.attachedTo === null)
        .map((o) => ({
          gear: o,
          // By meaning, not by key: keys are renumbered per card.
          equip: abilitiesOf(rules, o.card).find(
            (a): a is Extract<Ability, { kind: 'activated' }> =>
              a.kind === 'activated' && a.from === 'equip'),
        }))
        .filter((x) => x.equip !== undefined)
        .sort((a, b) => (a.equip!.cost.energy ?? 0) - (b.equip!.cost.energy ?? 0));

      for (const { gear, equip } of options) {
        const price = {
          energy: equip!.cost.energy ?? 0,
          power: Math.max(0, (equip!.cost.power ?? 0) - instr.discount),
          powerDomains: rules.index.get(gear.card).domains,
        };
        if (!pay(pool, price)) continue;
        gear.attachedTo = source.id;
        gear.attachedTurn = state.turn;
        if (!source.attachments.includes(gear.id)) source.attachments.push(gear.id);
        state.pendingTriggers.push({
          on: 'youAttachEquipment', player: controller, subject: gear.id,
        });
        ctx.didIt = true;
        break;
      }
      state.version++;
      break;
    }

    case 'attach': {
      const hosts = select(ctx, instr.target);
      const host = hosts[0];
      // 818. Without `what` the source attaches itself, which is Equip; with
      // it, something already chosen is attached to the source instead.
      const moving = instr.what ? select(ctx, instr.what)[0] : source;
      if (host && moving && moving.zone.kind === 'board' && moving.id !== host.id) {
        const source = moving;
        source.attachedTo = host.id;
        source.attachedTurn = state.turn;
        if (!host.attachments.includes(source.id)) host.attachments.push(source.id);
        state.pendingTriggers.push({
          on: 'youAttachEquipment', player: controller, subject: source.id,
        });
        source.location = host.location;
        state.version++;
        ctx.didIt = true;
      }
      break;
    }

    case 'burn': {
      const burned: ObjectId[] = [];
      ops.burn(state, rules, playerFor(ctx, instr.who), instr.count, burned);
      if (instr.forEachBurned) {
        for (const id of burned) {
          const card = state.objects[id];
          if (!card) continue;
          if (!matchesCardDef(rules.index.get(card.card), instr.forEachBurned.filter)) continue;
          runEffects({ ...ctx, chosen: [id] }, instr.forEachBurned.then);
        }
      }
      break;
    }

    case 'spendXp': {
      const player = state.players[controller];
      if (player.xp >= instr.amount) {
        player.xp -= instr.amount;
        ctx.didIt = true;
        state.version++;
      }
      break;
    }

    case 'prevent':
      // Modelled as temporary Might, which is the closest the engine has to a
      // damage shield and produces the same survival outcome in combat.
      for (const target of select(ctx, instr.target)) {
        ops.addMightMod(state, target, instr.amount, 'turn', source.id);
      }
      break;

    case 'choose': {
      // The selection was made at finalization; this just marks that the
      // instruction list has a referent for a following "it".
      const picked = select(ctx, instr.target);
      ctx.didIt = picked.length > 0;
      // 152. A chosen battlefield names a *place*, which a later instruction
      // can still refer to after `chosen` has moved on to something else.
      const place = picked.find((o) => rules.index.get(o.card).type === 'battlefield');
      if (place?.location) ctx.chosenPlace = place.location;
      break;
    }

    case 'armNextTime': {
      if (instr.on === 'unitPlayed') {
        state.turnEffects.push({
          k: 'nextUnitPlayed', player: controller,
          source: source.id, effects: instr.effects,
        });
        state.version++;
        break;
      }
      // A watcher with nothing to watch is not armed at all, rather than armed
      // against everything.
      for (const target of select(ctx, instr.target ?? { s: 'chosen' })) {
        if (instr.on === 'winsCombat') {
          state.turnEffects.push({
            k: 'winsCombat', player: controller, object: target.id,
            source: source.id, effects: instr.effects,
          });
          continue;
        }
        state.turnEffects.push(instr.on === 'dies'
          ? {
            k: 'nextDeath', player: controller, object: target.id,
            source: source.id, effects: instr.effects,
          }
          : {
            k: 'nextDamage', player: controller, object: target.id,
            source: source.id, effects: instr.effects,
          });
      }
      state.version++;
      break;
    }

    case 'delay':
      state.delayed.push({
        player: instr.who === 'opponent' ? OPPONENT(controller) : controller,
        when: instr.when,
        ...(instr.phase ? { phase: instr.phase } : {}),
        createdTurn: state.turn,
        source: source.id,
        // "Banish it. When they hold, return **it**" -- the referent has to
        // travel with the effect, or the delayed half has nothing to act on.
        ...(instr.keepChosen ? { chosen: [...ctx.chosen] } : {}),
        effects: instr.effects,
      });
      break;

    case 'thisTurn': {
      // The effect names its own player slot relative to whoever created it,
      // unless it says otherwise. A `source` slot is filled here too: the card
      // that armed it cannot be known when the card was compiled, and its
      // instructions resolve with that card as their source.
      const armed = { ...instr.effect, player: playerFor(ctx, instr.who) };
      if ('source' in armed) armed.source = source.id;
      state.turnEffects.push(armed);
      break;
    }

    case 'setMight': {
      const to = instr.from
        ? select(ctx, instr.from).map((o) => effectiveMight(state, rules, o))[0]
        : instr.amount;
      if (to !== undefined) {
        for (const target of select(ctx, instr.target)) {
          const now = effectiveMight(state, rules, target);
          const delta = to - now;
          if (delta === 0 || (instr.raiseOnly && delta < 0)) continue;
          ops.addMightMod(state, target, delta, instr.duration, source.id);
        }
      }
      state.version++;
      break;
    }

    case 'grantTag':
      for (const target of select(ctx, instr.target)) {
        target.tags = [...(target.tags ?? []), instr.tag];
      }
      state.version++;
      break;

    case 'preventMove':
      for (const target of select(ctx, instr.target)) {
        state.turnEffects.push({ k: 'cantMove', player: controller, object: target.id });
      }
      state.version++;
      break;

    case 'dealUnless': {
      for (const target of select(ctx, instr.target)) {
        // 355.12. The choice belongs to the target's controller. Lethal damage
        // is worth more than the price; anything less is not.
        const lethal = instr.amount >= Math.max(1, mightNow(ctx, target) - target.damage);
        if (lethal) {
          runEffects({ ...ctx, controller: target.controller }, instr.instead);
        } else {
          ops.deal(state, target, instr.amount, source.id);
          if (hasLethalDamage(state, rules, target)) ctx.lastDealKilled = true;
        }
      }
      break;
    }

    case 'grantAbility':
      for (const target of select(ctx, instr.target)) {
        state.turnEffects.push({
          k: 'grantedAbility', player: controller,
          object: target.id, ability: instr.ability,
        });
      }
      state.version++;
      break;

    case 'multiplyDamage':
      for (const target of select(ctx, instr.target)) {
        state.turnEffects.push({
          k: 'damageMultiplier', player: controller, object: target.id, factor: instr.factor,
        });
      }
      state.version++;
      break;

    case 'preventDamage':
      for (const target of select(ctx, instr.target)) {
        state.turnEffects.push({
          k: 'damageShield', player: controller, object: target.id, amount: instr.amount,
        });
      }
      state.version++;
      break;

    case 'shield':
      for (const target of select(ctx, instr.target)) {
        target.mods.push({
          kind: 'shield', amount: 1, duration: instr.duration, source: source.id,
        });
      }
      state.version++;
      break;

    case 'victoryScore':
      // 195. Both players race the same number, so this moves the finish line
      // for everyone rather than giving one side a different target.
      state.config.victoryScore += instr.by;
      state.version++;
      break;

    case 'extraTurn':
      // 314. Queued rather than taken now: the turn that grants it has to end
      // first, and the cleanup loop is what decides when that is.
      state.extraTurns.push(controller);
      state.version++;
      break;

    case 'winGame': {
      const who = instr.who ? playerFor(ctx, instr.who) : controller;
      state.winner = who;
      state.waiting = { kind: 'gameOver' };
      state.events.push({ t: 'gameOver', winner: who, reason: 'card effect' });
      break;
    }

    case 'lookAtFacedown':
      // 811. Nothing is hidden from the engine; the look is announced so a UI
      // can show it, and so the sentence is accounted for.
      state.events.push({
        t: 'lookFacedown', player: controller, at: playerFor(ctx, instr.who),
      });
      break;

    case 'revealHand':
      // Hands are private (108.7.c); revealing makes one public for the moment.
      // Nothing in the engine hides information from itself, so this only has
      // to be recorded rather than enforced.
      ctx.revealed = [...zoneList(state, 'hand', playerFor(ctx, instr.who))];
      break;

    case 'payAnyAmount': {
      // Nothing here can prompt, so it pays everything available -- which is
      // what a player does with an X cost they have already decided to use.
      const pool = state.players[controller].pool;
      const available = instr.of === 'energy'
        ? pool.energy
        : pool.any + DOMAINS.reduce((n, d) => n + pool.power[d], 0);
      if (available > 0) {
        pay(pool, instr.of === 'energy'
          ? { energy: available, power: 0, powerDomains: [] }
          : { energy: 0, power: available, powerDomains: [] });
        ctx.paidAmount = available;
        runEffects(ctx, instr.then);
      }
      break;
    }

    case 'payTo': {
      // 204.3. Pay first; the linked effect only happens if the cost was met.
      if (payCost(ctx, instr.cost)) {
        ctx.didIt = true;
        runEffects(ctx, instr.then);
      } else {
        ctx.didIt = false;
      }
      break;
    }

    case 'switchOn': {
      // 355.3. The referent picks the branch -- its domain or its card type.
      const it = ctx.chosen.map((id) => state.objects[id]).find(Boolean);
      if (!it) break;
      const def = rules.index.get(it.card);
      const key = instr.on === 'domain' ? (def.domains[0] ?? '') : def.type;
      const hit = instr.cases.find((c) => c.key === key);
      if (hit) runEffects(ctx, hit.then);
      break;
    }

    case 'chooseMode': {
      // 355.3. The mode is a choice its controller makes, so it is asked. A
      // modal card whose mode was picked for it is a different card.
      if (instr.modes.length === 0) break;
      const how = instr.count ?? 1;
      const left = instr.modes.map((_, i) => i);
      for (let n = 0; n < how && left.length > 0; n++) {
        const i = left.length === 1
          ? left[0]!
          : answer(ctx, left, 'Choose a mode.',
            instr.labels ? { labels: instr.labels } : {});
        const mode = instr.modes[i];
        if (mode) runEffects(modeContext(ctx, mode), mode);
        // 355.3.a. "Choose two different modes" may not take the same one
        // twice; a card that may repeat a mode leaves the list alone.
        if (instr.distinct !== false) left.splice(left.indexOf(i), 1);
      }
      break;
    }

    case 'if':
      if (testCondition(ctx, instr.cond)) runEffects(ctx, instr.then);
      else if (instr.else) runEffects(ctx, instr.else);
      break;

    case 'forEach': {
      // A snapshot first: the body can kill or move what it iterates over, and
      // the list must not shift underneath the loop.
      const over = boardObjects(state)
        .filter((o) => matchesFilter(state, rules, o, instr.of, source))
        .map((o) => o.id);
      for (const id of over) {
        if (!state.objects[id]) continue;
        runEffects({ ...ctx, chosen: [id] }, instr.body);
      }
      state.version++;
      break;
    }

    case 'repeat':
      for (let i = 0; i < instr.times; i++) runEffects(ctx, instr.body);
      break;

    case 'unimplemented':
      break;

    default: {
      // Exhaustiveness guard: a new op added to the union without a case here
      // becomes a compile error, not a silent no-op at runtime.
      const _never: never = instr;
      void _never;
    }
  }
}

// -- helpers -----------------------------------------------------------------

/** Reads a quantity from the game state for a dynamic amount. */
/** Wherever an earlier choice in the same sentence is standing. */
function chosenLocation(ctx: EffectContext): Location | null {
  for (const id of ctx.chosen) {
    const o = ctx.state.objects[id];
    if (o?.location && o.location.kind === 'battlefield') return o.location;
  }
  return null;
}

/**
 * 152.4. "Move a unit to a battlefield" names no battlefield, so the mover
 * chooses. Nothing here can prompt, so it takes the one canonical reading, the
 * same way combat damage assignment does: a unit you control goes where your
 * side is already strongest, and a unit you don't goes where it helps its
 * controller least. Never the location it is already at -- that is not a move.
 */
function pickBattlefield(
  ctx: EffectContext, target: GameObject,
  /** 187. "To a battlefield you control" -- only those, and none if there are none. */
  mustControl = false,
  /**
   * 152. "To a battlefield where you have units" -- where the *mover* has
   * units, which is a different question from who controls the place.
   */
  mustOccupy = false,
): Location | null {
  const { state, rules } = ctx;
  const friendly = target.controller === ctx.controller;
  const owner = target.controller;
  let best: Location | null = null;
  let bestScore = 0;
  for (let i = 0; i < state.battlefields.length; i++) {
    const at = battlefieldLocation(i);
    if (sameLocation(at, target.location)) continue;
    if (mustControl && state.battlefields[i]?.controller !== ctx.controller) continue;
    if (mustOccupy && !boardObjects(state).some((o) => o.controller === ctx.controller
      && isUnitType(rules.index.get(o.card).type) && sameLocation(o.location, at))) continue;
    let units = 0;
    for (const o of boardObjects(state)) {
      if (o.controller === owner && isUnitType(rules.index.get(o.card).type)
        && sameLocation(o.location, at)) units++;
    }
    // Reinforce for your own, isolate for theirs; ties go to the first
    // battlefield so the result stays deterministic.
    const score = friendly ? units + 1 : state.battlefields.length * 4 - units;
    if (best === null || score > bestScore) { best = at; bestScore = score; }
  }
  return best;
}

function dynamicAmount(ctx: EffectContext, from: DynamicAmount): number {
  // "That much" is whatever the payment just before it came to, which only the
  // running context knows.
  if (from === 'paidAmount') return ctx.paidAmount ?? 0;
  if (from === 'chosenMight') {
    const first = ctx.chosen[0] !== undefined ? ctx.state.objects[ctx.chosen[0]] : undefined;
    return first ? effectiveMight(ctx.state, ctx.rules, first) : 0;
  }
  if (from === 'damageOnChosen') {
    const first = ctx.chosen[0] !== undefined ? ctx.state.objects[ctx.chosen[0]] : undefined;
    return first ? first.damage : 0;
  }
  if (from === 'chosenSpellCost') {
    // 327. The chosen thing that is on the chain -- which is what "that spell"
    // names, whatever order the card chose its referents in.
    const spell = ctx.chosen
      .map((id) => ctx.state.objects[id])
      .find((o) => o && ctx.state.chain.some((i) => i.source === o.id));
    return spell ? ctx.rules.index.get(spell.card).cost.energy : 0;
  }
  if (from === 'chosenCost') {
    const first = ctx.chosen[0] !== undefined ? ctx.state.objects[ctx.chosen[0]] : undefined;
    return first ? ctx.rules.index.get(first.card).cost.energy : 0;
  }
  return countDynamic(ctx.state, ctx.rules, ctx.source, ctx.controller, from);
}

function mightNow(ctx: EffectContext, target: GameObject): number {
  const def = ctx.rules.index.get(target.card);
  if (def.might === null) return 0;
  let m = def.might;
  for (const mod of target.mods) if (mod.kind === 'might') m += mod.amount;
  return m;
}

/** Damage still needed to make this unit lethal, for splitting damage. */
function mightGap(ctx: EffectContext, target: GameObject): number {
  return Math.max(1, mightNow(ctx, target) - target.damage);
}

/**
 * 203. Pays a cost within an instruction, all components or none. Returns false
 * without spending anything if any part cannot be met.
 */
function payCost(ctx: EffectContext, cost: ActivationCost): boolean {
  const { state, rules, source, controller } = ctx;
  const player = state.players[controller];

  const resource = {
    energy: cost.energy ?? 0,
    power: cost.power ?? 0,
    powerDomains: cost.powerDomain && cost.powerDomain !== 'any'
      ? [cost.powerDomain === 'self'
        ? (rules.index.get(source.card).domains[0] ?? 'fury')
        : cost.powerDomain]
      : [],
  };

  // Check everything before spending anything.
  // 204. An ability paying a cost mid-resolution spends as an ability.
  const purpose: SpendPurpose = isUnitType(rules.index.get(source.card).type)
    ? 'unitAbility' : 'ability';
  const showdown = state.showdownState === 'showdown';
  if (!canPay(player.pool, resource, purpose, showdown)) return false;
  if (cost.exhaust && (source.exhausted || source.zone.kind !== 'board')) return false;
  if (cost.spendBuff && !source.statuses.includes('buffed')) return false;
  if (cost.spendEmpower && !source.statuses.includes('empowered')) return false;
  // 441. Nothing to spend if it is not Empowered in the first place.
  if (cost.disempowerSelf && !source.statuses.includes('empowered')) return false;
  // 440. Burning off an empty deck is not a price that can be met.
  if (cost.burn && zoneList(state, 'mainDeck', controller).length < cost.burn) return false;
  if (cost.spendXp !== undefined && player.xp < cost.spendXp) return false;
  if (cost.discard
    && discardCandidates(state, rules, controller, cost.discardFilter).length < cost.discard) {
    return false;
  }
  if (cost.recycleFromTrash
    && zoneList(state, 'trash', controller).length < cost.recycleFromTrash) return false;
  if ((cost.killSelf || cost.recycleSelf || cost.banishSelf) && source.zone.kind !== 'board') return false;
  if (cost.sacrifice
    && sacrificeCandidates(state, rules, source, cost.sacrifice).length
      < cost.sacrifice.count) return false;

  pay(player.pool, resource, purpose, showdown);
  player.spentThisTurn.energy += resource.energy;
  player.spentThisTurn.power += resource.power;
  if (cost.exhaust) ops.exhaust(state, source);
  if (cost.spendBuff) {
    ops.spendBuff(source);
  }
  if (cost.spendEmpower) ops.disempower(state, source);
  if (cost.sacrifice) {
    const victims = sacrificeCandidates(state, rules, source, cost.sacrifice)
      .slice(0, cost.sacrifice.count);
    for (const victim of victims) {
      switch (cost.sacrifice.action) {
        case 'kill': ops.kill(state, rules, victim, source.id); break;
        case 'exhaust': ops.exhaust(state, victim); break;
        case 'return': ops.returnToHand(state, rules, victim); break;
        case 'spendBuff': ops.spendBuff(victim); break;
      }
    }
  }
  if (cost.spendXp !== undefined) player.xp -= cost.spendXp;
  if (cost.discard) {
    const payable = discardCandidates(state, rules, controller, cost.discardFilter);
    ops.discard(state, rules, controller, payable.slice(0, cost.discard));
  }
  if (cost.recycleFromTrash) {
    ops.recycle(state, rules, [...zoneList(state, 'trash', controller)].slice(0, cost.recycleFromTrash));
  }
  if (cost.killSelf) ops.kill(state, rules, source, source.id);
  if (cost.recycleSelf) ops.recycle(state, rules, [source.id]);
  if (cost.banishSelf) ops.banish(state, rules, source, source.id);
  // 440. Burn is a price paid off the top of the Main Deck.
  if (cost.burn) ops.burn(state, rules, controller, cost.burn);
  // 441. Spending the Empowered status is what "Disempower this" charges.
  if (cost.disempowerSelf) ops.disempower(state, source);
  state.version++;
  return true;
}

/** Filters a card by its printed characteristics, for zone searches. */
function matchesCardFilter(
  ctx: EffectContext, o: GameObject, filter: ObjectFilter,
): boolean {
  const def = ctx.rules.index.get(o.card);
  if (filter.type) {
    const ok = filter.type === 'unit' ? isUnitType(def.type)
      : filter.type === 'gear' ? def.type === 'gear'
        : filter.type === 'spell' ? def.type === 'spell' || def.type === 'signatureSpell'
          : filter.type === 'rune' ? def.type === 'rune'
            : true;
    if (!ok) return false;
  }
  if (filter.maxEnergyCost !== undefined && def.cost.energy > filter.maxEnergyCost) return false;
  if (filter.hasKeyword && !def.keywords.includes(filter.hasKeyword)) return false;
  if (filter.tag && !def.tags.includes(filter.tag)) return false;
  return true;
}

/** Moves a card that a reveal or search turned up into its destination. */
function moveRevealed(
  ctx: EffectContext, o: GameObject, to: 'hand' | 'board',
  /** 349.4. "You may play it here" -- the source's battlefield, not the base. */
  here = false,
): void {
  const { state, rules, controller, source } = ctx;

  // Through moveToZone rather than editing the lists here. Setting the zone
  // and pushing by hand double-listed any card that was already in its
  // destination, and a later play removed only one entry -- leaving a card on
  // the board still listed in hand, which legalActions then offered as
  // playable and applyAction refused.
  if (to === 'hand') {
    ops.obj(state, o.id);
    moveToZone(state, rules, o, { kind: 'hand', player: o.owner });
    return;
  }

  moveToZone(state, rules, o, { kind: 'board' });
  o.controller = controller;
  o.location = here && source.location?.kind === 'battlefield'
    ? source.location
    : baseLocation(controller);
  o.exhausted = isUnitType(rules.index.get(o.card).type);
  state.events.push({
    t: 'play', player: controller, object: o.id, card: o.card, to: o.location,
  });
  state.version++;
}

/** The referent a preceding instruction settled on, if any. */
function firstChosen(ctx: EffectContext): GameObject | undefined {
  const id = ctx.chosen[0];
  return id !== undefined ? ctx.state.objects[id] : undefined;
}

function playerFor(ctx: EffectContext, who: Selector | undefined): PlayerId {
  if (!who) return ctx.controller;
  if (who.s === 'opponent') return OPPONENT(ctx.controller);
  if (who.s === 'chosenOwner') {
    // 056.2. The owner, not the controller: a card that has changed hands goes
    // back to whoever brought it, and that is who the effect names.
    const first = ctx.chosen[0] !== undefined ? ctx.state.objects[ctx.chosen[0]] : undefined;
    return first ? first.owner : ctx.controller;
  }
  return ctx.controller;
}

/**
 * Resolves a selector to concrete objects. `choose` selectors read from the
 * targets picked at finalization; anything left unmatched simply yields
 * nothing, which is the correct handling for a target that has since left the
 * board (055).
 */
export function select(ctx: EffectContext, sel: Selector): GameObject[] {
  const { state, rules, source } = ctx;

  switch (sel.s) {
    case 'self':
      return state.objects[source.id] ? [source] : [];

    case 'paidWith':
      return (ctx.paidWith ?? [])
        .map((id) => state.objects[id])
        .filter((o): o is GameObject => Boolean(o));

    case 'chosen':
    case 'choose': {
      const out: GameObject[] = [];
      for (const id of ctx.chosen) {
        const o = state.objects[id];
        // 355.9. A spell being countered or taken over is on the chain, not
        // the board, so restricting this to board objects meant "counter a
        // spell" could never find anything to counter.
        if (o && (o.zone.kind === 'board' || o.zone.kind === 'chain')) out.push(o);
      }
      return out;
    }

    case 'all':
      // The chosen referent is passed through: "move all enemy units with
      // less [M] than the chosen unit" compares against it, not the source.
      return boardObjects(state).filter((o) =>
        matchesFilter(state, rules, o, sel.of, source, firstChosen(ctx)));

    default:
      return [];
  }
}

/**
 * Candidate targets for a `choose` selector, used both to validate a submitted
 * action and to enumerate options for the AI.
 */
export function candidates(
  state: GameState, rules: Rules, source: GameObject, filter: ObjectFilter,
  /**
   * A referent picked earlier in the same instruction list. "Kill an enemy
   * unit with less [M] than it" is about that one, and without it every
   * candidate is rejected -- so targeting has to hand it down.
   */
  chosen?: GameObject,
): GameObject[] {
  // Every object, not just the board: a spell waiting on the chain is a legal
  // target and is not a board object. `isTargetable` decides what qualifies.
  const pool: GameObject[] = [];
  for (const key of Object.keys(state.objects)) {
    const o = state.objects[Number(key)];
    if (o) pool.push(o);
  }

  // A filter that names a zone off the board is asking about cards -- "all
  // units in your trash" -- so `isTargetable`, which only admits permanents and
  // chain spells, would reject every one of them and the effect would do
  // nothing at all.
  const offBoard = filter.zone !== undefined && filter.zone !== 'board';

  return pool.filter((o) => {
    if (!offBoard && !isTargetable(state, rules, o)) return false;
    if (cantBeChosenBy(state, rules, o, source.controller)) return false;
    return matchesFilter(state, rules, o, filter, source, chosen);
  });
}

function isTargetable(state: GameState, rules: Rules, o: GameObject): boolean {
  const def = rules.index.get(o.card);
  // A spell is targetable while it waits on the chain -- that is the only
  // moment anything can counter it or take it over.
  if (o.zone.kind === 'chain') return isSpellType(def.type);
  if (o.zone.kind !== 'board') return false;
  return isUnitType(def.type) || def.type === 'gear' || def.type === 'rune'
    || def.type === 'battlefield' || def.type === 'legend';
}

function testCondition(ctx: EffectContext, cond: Condition): boolean {
  const { state, rules, source, controller } = ctx;

  // Everything judged from the board alone is shared with the passive system,
  // so a condition can never mean one thing to an ability and another to a
  // passive gated on the same words.
  const stat = testStaticCondition(state, rules, source, controller, cond);
  if (stat !== null) return stat;

  switch (cond.c) {
    case 'attachedThisTurn':
      // 818. On an Equipment, about itself: when it landed on its host.
      return source.attachedTo !== null && source.attachedTurn === state.turn;

    case 'chosenAlone': {
      const it = ctx.chosen.map((id) => state.objects[id]).find(Boolean);
      if (!it?.location) return false;
      return !boardObjects(state).some((o) =>
        o.id !== it.id && o.controller === it.controller
        && isUnitType(rules.index.get(o.card).type)
        && sameLocation(o.location, it.location));
    }
    case 'killedIt':
      return ctx.lastDealKilled;
    case 'didIt':
      return ctx.didIt;
    case 'paidAdditionalCost':
      return ctx.paidAdditionalCost;
    case 'revealedTag': {
      // The cards this effect has looked at, not the board: "if you revealed a
      // Bird, Cat, Dog, or Poro".
      return ctx.revealed.some((id) => {
        const o = state.objects[id];
        return o !== undefined
          && cond.tags.some((t) => rules.index.get(o.card).tags.includes(t));
      });
    }
    case 'chosenMightAtMost': {
      const picked = ctx.chosen
        .map((id) => state.objects[id])
        .filter((o): o is GameObject => Boolean(o));
      return picked.length > 0
        && picked.every((o) => effectiveMight(state, rules, o) <= cond.amount);
    }
    case 'chosenMightLessThanSelf': {
      const mine = effectiveMight(state, rules, source);
      const picked = ctx.chosen
        .map((id) => state.objects[id])
        .filter((o): o is GameObject => Boolean(o));
      return picked.length > 0
        && picked.every((o) => effectiveMight(state, rules, o) < mine);
    }
    case 'anyChosenIs': {
      // 355. "At least one of them": any of the picks, where `chosenIs` wants
      // all of them.
      return ctx.chosen
        .map((id) => state.objects[id])
        .filter((o): o is GameObject => Boolean(o))
        .some((o) => matchesFilter(state, rules, o, cond.filter, source));
    }
    case 'chosenIs': {
      // The referent a preceding instruction settled on: "Choose an enemy
      // unit. If it is stunned, kill it."
      const picked = ctx.chosen
        .map((id) => state.objects[id])
        .filter((o): o is GameObject => Boolean(o));
      return picked.length > 0
        && picked.every((o) => matchesFilter(state, rules, o, cond.filter, source));
    }
    case 'revealedIs': {
      // 353.4. The reveal put cards in `revealed`; this asks what they were.
      const shown = ctx.revealed
        .map((id) => state.objects[id])
        .filter((o): o is GameObject => Boolean(o));
      return shown.some((o) => matchesFilter(state, rules, o, cond.filter, source));
    }
    case 'targetDied':
      return ctx.chosen.every((id) => {
        const o = state.objects[id];
        return !o || o.zone.kind !== 'board' || hasLethalDamage(state, rules, o);
      });
    default:
      return false;
  }
}
